import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Table,
  TableBody,
  Box,
  TableCell,
  TableContainer,
  TableHead,
  MenuItem,
  TableRow,
  CircularProgress,
  TextField,
  Paper,
  InputLabel,
  Select,
  FormControl,
  AppBar,
  Toolbar,
  Typography,
  Modal,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import LoadingBar from 'react-top-loading-bar';
import base64 from 'base64-js';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import LocationOnIcon from "@mui/icons-material/LocationOn";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// ── Employee User Guide Steps ──────────────────────────────────────────────
const employeeGuideData = [
  {
    section: "🔍 Search & Filter",
    steps: [
      {
        label: "Search Bar",
        desc: "Type an employee name in the search box to instantly filter the table. The search is case-insensitive and updates results in real time as you type.",
      },
      {
        label: "Branch Dropdown (Master Users Only)",
        desc: "Visible only for Master/Admin users. Select a branch from the dropdown in the top-right of the header to load employees belonging to that specific branch. Auto-fills with the first branch on load.",
      },
    ],
  },
  {
    section: "➕ Adding Employees",
    steps: [
      {
        label: "Add Employee Button",
        desc: "Click 'ADD EMPLOYEE' to navigate to the employee registration form. This button is disabled if you do not have Add (Write) permission for this module.",
      },
      {
        label: "Authorization — Add",
        desc: "If you see 'You are not Authorized to Add' in the tooltip, your account does not have write permission. Contact your administrator to grant access.",
      },
    ],
  },
  {
    section: "📋 Employee Table",
    steps: [
      {
        label: "Employee Code",
        desc: "Unique identifier code assigned to each employee in the system.",
      },
      {
        label: "Employee Name",
        desc: "Full name of the employee. Use the search bar to filter by name.",
      },
      {
        label: "Mobile No.",
        desc: "Registered mobile number of the employee.",
      },
      {
        label: "Email-id",
        desc: "Employee's registered email address. Displays '--' if no email is provided.",
      },
      {
        label: "Department Name",
        desc: "The department to which the employee is assigned (e.g. Teaching, Admin, Accounts).",
      },
      {
        label: "Designation Name",
        desc: "The job title or role of the employee within the department (e.g. Teacher, Principal, Clerk).",
      },
      {
        label: "Date of Joining",
        desc: "The official date on which the employee joined the organization.",
      },
    ],
  },
  {
    section: "✏️ Edit & 🗑️ Delete Actions",
    steps: [
      {
        label: "Edit Button",
        desc: "Click the orange 'Edit' button to navigate to the employee edit form with pre-filled data. Disabled if you do not have Modify permission.",
      },
      {
        label: "Authorization — Edit",
        desc: "If the Edit button is disabled, hover to see 'You are not authorized to edit'. Request Modify permission from your administrator.",
      },
      {
        label: "Delete Button",
        desc: "Click the red 'Delete' button to open a confirmation modal before permanently removing an employee record. Disabled if you do not have Delete permission.",
      },
      {
        label: "Delete Confirmation Modal",
        desc: "A popup appears showing the employee name and asks 'Are you sure?'. Click 'Yes, Delete' to confirm or 'Cancel' to go back safely.",
      },
      {
        label: "Authorization — Delete",
        desc: "If the Delete button is disabled, your account does not have delete permission. Contact your administrator.",
      },
    ],
  },
  {
    section: "📄 Pagination",
    steps: [
      {
        label: "Rows Per Page",
        desc: "Use the 'Rows per page' dropdown to choose how many employees appear per page. Options: 5, 10, 25, or 50 rows. Changing this resets to page 1.",
      },
      {
        label: "Prev / Next Buttons",
        desc: "Use 'Prev' and 'Next' buttons to navigate between pages. The current page and total pages are shown between the buttons.",
      },
    ],
  },
  {
    section: "🔔 Notifications",
    steps: [
      {
        label: "Success Snackbar",
        desc: "A green notification appears at the top-center when an employee is deleted successfully.",
      },
      {
        label: "Error Snackbar",
        desc: "A red notification appears when an API error occurs, a record is not found, or an action fails (e.g. delete failed, invalid country name).",
      },
      {
        label: "Warning Snackbar",
        desc: "A yellow/orange notification appears for authorization warnings (e.g. invalid country name response).",
      },
    ],
  },
];

// ── Employee User Guide Modal ──────────────────────────────────────────────
const EmployeeUserGuide = ({ open, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: "14px",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
      },
    }}
  >
    {/* ── Gradient header matching AppBar theme ── */}
    <DialogTitle
      sx={{
        background: "linear-gradient(135deg, #0B1F3D 0%, #1565c0 60%, #26c6da 100%)",
        color: "#fff",
        fontWeight: 700,
        fontSize: "1.1rem",
        textAlign: "center",
        py: 2.5,
        letterSpacing: "0.04em",
      }}
    >
      👨‍💼 Employee Module — User Guide
    </DialogTitle>

    {/* ── Scrollable content ── */}
    <DialogContent
      dividers
      sx={{
        px: 3,
        py: 2.5,
        maxHeight: "62vh",
        overflowY: "auto",
        backgroundColor: "#fafafa",
      }}
    >
      {employeeGuideData.map((group, gi) => (
        <Box key={gi} sx={{ mb: gi < employeeGuideData.length - 1 ? 3 : 0 }}>

          {/* Section heading */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.95rem",
              color: "#1565c0",
              mb: 1.5,
              pb: 0.5,
              borderBottom: "2px solid #bbdefb",
            }}
          >
            {group.section}
          </Typography>

          {/* Step list */}
          {group.steps.map((step, si) => (
            <Box
              key={si}
              sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.2 }}
            >
              {/* Bullet dot */}
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#1976d2",
                  mt: "5px",
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: "0.875rem", color: "#333", lineHeight: 1.6 }}>
                <Box component="span" sx={{ fontWeight: 700, color: "#111" }}>
                  {step.label}
                </Box>
                {" — "}
                {step.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}

      {/* ── Bottom tip box ── */}
      <Box
        sx={{
          mt: 2.5,
          p: 1.5,
          borderRadius: "8px",
          backgroundColor: "#e3f2fd",
          border: "1px solid #90caf9",
        }}
      >
        <Typography sx={{ fontSize: "0.8rem", color: "#1565c0", fontStyle: "italic" }}>
          💡 <strong>Tip:</strong> Use the <strong>Search Bar</strong> to quickly find an employee by name. Master users should select the correct <strong>Branch</strong> before searching to ensure accurate results.
        </Typography>
      </Box>
    </DialogContent>

    {/* ── Close button ── */}
    <DialogActions sx={{ justifyContent: "center", py: 2, backgroundColor: "#fafafa" }}>
      <Button
        onClick={onClose}
        variant="contained"
        sx={{
          background: "linear-gradient(45deg, #0B1F3D 30%, #1976d2 90%)",
          borderRadius: "25px",
          px: 5,
          py: 1,
          fontWeight: 600,
          textTransform: "none",
          fontSize: "0.9rem",
          boxShadow: "0 3px 8px rgba(11,31,61,0.35)",
          "&:hover": {
            background: "linear-gradient(45deg, #0a1a33 30%, #1565c0 90%)",
          },
        }}
      >
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

const Employee = () => {
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [authorization, setAuthorization] = useState([]);
  const [canDelete, setCanDelete] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [canAdd, setCanAdd] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isMaster, setIsMaster] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // Controls the Employee User Guide modal open/close
  const [guideOpen, setGuideOpen] = useState(false);

  const { encodedFormId } = useParams();

  const decodeFormId = (encodedFormId) => {
    const bytes = base64.toByteArray(encodedFormId);
    return new TextDecoder().decode(bytes);
  };
  const formId = decodeFormId(encodedFormId);

  const fetchBranches = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        setBranches(data);

        // 🔹 Auto-fill first branch for master user when branches are loaded
        if (data.length > 0) {
          setSelectedBranchId(data[0].branchId);
        }
      } else {
        console.error('Failed to fetch branches');
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem('branchId');
      const response = await fetch(`${apiUrl}/Employee/GetEmployee?Branch_Id=${branchId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error(`Error fetching employee data: ${response.status}`);
      }

      const responseData = await response.json();

      // Check if responseData is an array (direct response) or an object with data property
      if (Array.isArray(responseData)) {
        // Backend returned array directly
        setData(responseData);
        setFilteredData(responseData);
      } else if (responseData.data === null && responseData.msg === "Record Not Found") {
        // Backend returned error object
        console.error('Record Not Found');
        setSnackbarMessage('Record Not Found');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
        setData([]);
        setFilteredData([]);
      } else {
        // Backend returned object with data property
        setData(responseData.data || []);
        setFilteredData(responseData.data || []);
      }
    } catch (error) {
      console.error('API request error:', error);
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
      setData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
      setLoadingBarProgress(100);
    }
  };

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          formId,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setAuthorization(responseData);
        const authorizationData = responseData[0];
        setCanDelete(authorizationData.uDelete === 1);
        setCanEdit(authorizationData.uModify === 1);
        setCanAdd(authorizationData.uWrite === 1);
      } else {
        console.error('Country name incorrect');
        setSnackbarMessage('Invalid country name');
        setOpenSnackbar(true);
        setSnackbarSeverity("warning");
      }
    } catch (error) {
      console.error('API request error:', error);
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  useEffect(() => {
    if (selectedBranchId) {
      fetchData();
    }
  }, [selectedBranchId]);

  useEffect(() => {
    const isMasterUser = sessionStorage.getItem('is_Master') === 'true';
    setIsMaster(isMasterUser);

    if (isMasterUser) {
      fetchBranches();
    } else {
      const branchId = sessionStorage.getItem('branchId');
      setSelectedBranchId(branchId);
    }

    fetchData();
    Authorizer();
  }, []);

  // Function to handle search logic
  const handleSearch = (query) => {
    setSearchQuery(query);
    const filtered = data.filter((employee) =>
      employee.employeeName.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredData(filtered);
  };

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Function to handle showing delete confirmation modal
  const handleShowDeleteModal = (employee) => {
    setSelectedEmployee(employee);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  // Function to handle delete confirmation
  const handleDelete = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      setLoadingBarProgress(30);
      const response = await fetch(
        `${apiUrl}/Employee/Id?EmployeeId=${selectedEmployee.employeeID}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
        }
      );

      if (response.ok) {
        fetchData();
        setShowModal(false);
        setLoadingBarProgress(100);
        setSnackbarMessage('Employee data deleted successfully!');
        setOpenSnackbar(true);
        setSnackbarSeverity("success");
      } else {
        setLoadingBarProgress(0);
        console.error('Delete failed');
        setSnackbarMessage('Failed to delete employee');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error('API request error:', error);
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // reset to page 1
  };

  return (
    <div className="container">

      {/* ── Employee User Guide Modal ── */}
      <EmployeeUserGuide open={guideOpen} onClose={() => setGuideOpen(false)} />

      <AppBar
        position="static"
        sx={{
          backgroundColor: "#0B1F3D",
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3 }}>

          {/* Left: Title + Info Button */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                letterSpacing: "0.5px",
                color: "#ffffff",
              }}
            >
              Employee
            </Typography>
            <Tooltip title="View User Guide" placement="right" arrow>
              <IconButton
                onClick={() => setGuideOpen(true)}
                size="small"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  border: "2px solid rgba(255,255,255,0.7)",
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: "0.85rem",
                  fontFamily: "serif",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.3)",
                    border: "2px solid #fff",
                    transform: "scale(1.12)",
                    boxShadow: "0 0 12px rgba(255,255,255,0.4)",
                  },
                }}
              >
                i
              </IconButton>
            </Tooltip>
          </Box>

          {/* Center/Right: Branch Dropdown + ℹ️ Button */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>

            {/* Right: Branch Dropdown */}
            {isMaster && (
              <Box sx={{ position: "relative" }}>
                <FormControl size="small" sx={{ width: 220 }}>
                  <InputLabel
                    sx={{
                      color: "rgba(255,255,255,0.6)",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      letterSpacing: "0.6px",
                      textTransform: "uppercase",
                      top: "-2px",
                      "&.Mui-focused": { color: "#64B5F6" },
                      "&.MuiFormLabel-filled": { color: "rgba(255,255,255,0.7)" },
                    }}
                  >
                    Branch
                  </InputLabel>
                  <Select
                    value={selectedBranchId || ""}
                    onChange={(e) => setSelectedBranchId(e.target.value)}
                    label="Branch"
                    startAdornment={
                      <InputAdornment position="start">
                        <LocationOnIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.5)", ml: 0.5 }} />
                      </InputAdornment>
                    }
                    sx={{
                      color: "#ffffff",
                      fontWeight: 500,
                      fontSize: "0.875rem",
                      letterSpacing: "0.3px",
                      backgroundColor: "rgba(255,255,255,0.07)",
                      backdropFilter: "blur(8px)",
                      borderRadius: "8px",
                      transition: "background-color 0.2s ease, box-shadow 0.2s ease",

                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255,255,255,0.15)",
                        borderWidth: "1px",
                        transition: "border-color 0.2s ease",
                      },
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.11)",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255,255,255,0.35)",
                        },
                      },
                      "&.Mui-focused": {
                        backgroundColor: "rgba(255,255,255,0.1)",
                        boxShadow: "0 0 0 3px rgba(100,181,246,0.18)",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#64B5F6",
                          borderWidth: "1.5px",
                        },
                      },
                      "& .MuiSelect-icon": {
                        color: "rgba(255,255,255,0.5)",
                        transition: "color 0.2s ease",
                      },
                      "&.Mui-focused .MuiSelect-icon": {
                        color: "#64B5F6",
                      },
                      "& .MuiSelect-select": {
                        py: 1.1,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          mt: 0.5,
                          borderRadius: "10px",
                          border: "1px solid rgba(11,31,61,0.12)",
                          boxShadow: "0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)",
                          overflow: "hidden",
                          "& .MuiList-root": { py: 0.5 },
                        },
                      },
                    }}
                  >
                    {branches.map((branch) => (
                      <MenuItem
                        key={branch.branchId}
                        value={branch.branchId}
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          color: "#0B1F3D",
                          px: 2,
                          py: 1.1,
                          mx: 0.5,
                          borderRadius: "6px",
                          transition: "background-color 0.15s ease",
                          "&:hover": {
                            backgroundColor: "rgba(11,31,61,0.07)",
                          },
                          "&.Mui-selected": {
                            backgroundColor: "rgba(25,118,210,0.1)",
                            color: "#1565C0",
                            fontWeight: 600,
                            "&:hover": {
                              backgroundColor: "rgba(25,118,210,0.15)",
                            },
                          },
                        }}
                      >
                        {branch.branchName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

           

          </Box>
        </Toolbar>
      </AppBar>

      <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6 }}>
        <LoadingBar progress={loadingBarProgress} color="rgb(152, 106, 182)" height={5} />
        <Box
          className="container mt-2"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          {/* Left Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            {/* Search */}
            <TextField
              variant="outlined"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: { sm: 300 },
                backgroundColor: '#f9f9f9',
              }}
            />
          </Box>

          {/* Right Section */}
          <Tooltip title={!canAdd && "You are not Authorized to Add"} arrow>
            <span>
              <Button
                variant="contained"
                onClick={() => navigate('/addemployee')}
                disabled={!canAdd}
                sx={{
                  width: { xs: '100%', md: 160 },
                  height: 50,
                  fontWeight: 'bold',
                }}
              >
                ADD EMPLOYEE
              </Button>
            </span>
          </Tooltip>
        </Box>

        <TableContainer component={Paper} style={{ marginTop: 30 }}>
          <Table sx={{
            borderCollapse: 'collapse', width: '100%',
            '& .MuiTableHead-root .MuiTableCell-root': {
              fontWeight: 'bold',
              backgroundColor: '#f5f5f5',
            },
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ border: '1px solid #ccc' }}><b>Employee Code</b></TableCell>
                <TableCell sx={{ border: '1px solid #ccc' }}><b>Employee Name</b></TableCell>
                <TableCell sx={{ border: '1px solid #ccc' }}><b>Mobile No.</b></TableCell>
                <TableCell sx={{ border: '1px solid #ccc' }}><b>Email-id</b></TableCell>
                <TableCell sx={{ border: '1px solid #ccc' }}><b>Department Name</b></TableCell>
                <TableCell sx={{ border: '1px solid #ccc' }}><b>Designation Name</b></TableCell>
                <TableCell sx={{ border: '1px solid #ccc' }}><b>Date of Joining</b></TableCell>
                <TableCell sx={{ border: '1px solid #ccc', textAlign: 'center' }}><b>Action</b></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((employee) => (
                  <TableRow key={employee.employeeID}>
                    <TableCell sx={{ border: '1px solid #ccc' }}>{employee.employeeCode}</TableCell>
                    <TableCell sx={{ border: '1px solid #ccc' }}>{employee.employeeName}</TableCell>
                    <TableCell sx={{ border: '1px solid #ccc' }}>{employee.mobileNo}</TableCell>
                    <TableCell sx={{ border: '1px solid #ccc' }}>{employee.emailID || '--'}</TableCell>
                    <TableCell sx={{ border: '1px solid #ccc' }}>{employee.departmentName}</TableCell>
                    <TableCell sx={{ border: '1px solid #ccc' }}>{employee.designationName}</TableCell>
                    <TableCell sx={{ border: '1px solid #ccc' }}>{employee.joinDate}</TableCell>
                    <TableCell sx={{ border: '1px solid #ccc', textAlign: 'center' }}>
                      <Tooltip title={canEdit ? '' : 'You are not authorized to edit'} arrow>
                        <span>
                          <Button
                            type="button"
                            variant="contained"
                            color="warning"
                            onClick={() => navigate(`/editemployee/${employee.employeeID}`)}
                            disabled={!canEdit}
                            size='small'
                            startIcon={<EditIcon />}
                            style={{ marginRight: '5px' }}
                          >
                            Edit
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title={canDelete ? '' : 'You are not authorized to delete'} arrow>
                        <span>
                          <Button
                            type="button"
                            variant="contained"
                            color="error"
                            onClick={() => handleShowDeleteModal(employee)}
                            startIcon={<DeleteIcon />}
                            disabled={!canDelete}
                            size='small'
                          >
                            Delete
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No Employees Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredData.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
            <TextField
              select
              label="Rows per page"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              sx={{ width: 150 }}
              SelectProps={{
                native: true,
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </TextField>

            <Box>
              <Typography variant="body2" sx={{ display: 'inline-block', marginRight: 2 }}>
                Page {page} of {Math.ceil(filteredData.length / rowsPerPage)}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => handleChangePage(null, page - 1)}
                disabled={page === 1}
                sx={{ mr: 1 }}
              >
                Prev
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleChangePage(null, page + 1)}
                disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Modal */}
      <Modal open={showModal} onClose={handleClose} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedEmployee && selectedEmployee.employeeName}</strong>?
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleDelete(handleDelete)}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

    </div>
  );
};

export default Employee;